import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import { ExpenseLabel, createExpenseLabel } from 'stores/ExpenseStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

const NAME_MAX = 64;
const DEFAULT_COLOR = '#ef6c00';

type LabelOption = ExpenseLabel & { inputValue?: string };

const filter = createFilterOptions<LabelOption>();

type Props = {
  labels: ExpenseLabel[];
  value: string;
  onChange: (labelId: string) => void;
  /** インライン作成したラベルを親の候補一覧に反映する */
  onLabelCreated: (label: ExpenseLabel) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

/**
 * 品目ラベルの単一セレクト（その場で新規作成可＝createExpenseLabel インライン・カラーピッカー）。
 * 経費登録/編集フォーム用。422（同名重複）はダイアログ内にインライン表示する。
 */
const ExpenseLabelSelect: React.FC<Props> = ({
  labels,
  value,
  onChange,
  onLabelCreated,
  required = false,
  error = false,
  helperText,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newColor, setNewColor] = React.useState(DEFAULT_COLOR);
  const [creating, setCreating] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | undefined>();

  const selected = labels.find((l) => l.expenseLabelId === value) ?? null;

  const openCreateDialog = (presetName: string) => {
    setNewName(presetName);
    setNewColor(DEFAULT_COLOR);
    setDialogError(undefined);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setDialogError('品目ラベル名を入力してください');
      return;
    }
    setCreating(true);
    try {
      const expenseLabelId = await createExpenseLabel({ name, color: newColor });
      const created: ExpenseLabel = { expenseLabelId, name, color: newColor };
      onLabelCreated(created);
      onChange(expenseLabelId);
      successDefault('品目ラベルを作成しました');
      setDialogOpen(false);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 422) {
        setDialogError('同じ名前の品目ラベルが既に存在します');
      } else {
        errorDefault('品目ラベルの作成に失敗しました', toErrorDetail(e));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Autocomplete<LabelOption, false, false, false>
        options={labels as LabelOption[]}
        value={selected as LabelOption | null}
        disabled={disabled}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(o, v) => o.expenseLabelId === v.expenseLabelId}
        onChange={(_, newValue) => {
          if (newValue?.inputValue !== undefined) {
            openCreateDialog(newValue.inputValue ?? '');
            return;
          }
          onChange(newValue ? newValue.expenseLabelId : '');
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const input = params.inputValue.trim();
          const exists = options.some((o) => o.name.toLowerCase() === input.toLowerCase());
          if (input !== '' && !exists) {
            filtered.push({
              expenseLabelId: `__create__${input}`,
              name: `「${input}」を新規作成`,
              color: DEFAULT_COLOR,
              inputValue: input,
            });
          }
          return filtered;
        }}
        renderOption={(optionProps, option) => {
          const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
          if (option.inputValue !== undefined) {
            return (
              <Box
                component="li"
                key={String(key)}
                {...rest}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>
                  {option.name}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              component="li"
              key={String(key)}
              {...rest}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: option.color,
                  flexShrink: 0,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="品目ラベル"
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="品目ラベルを選択 / 新規作成..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        noOptionsText="該当する品目ラベルがありません"
        sx={{ width: '100%' }}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            品目ラベルを新規作成
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="ラベル名"
              size="small"
              fullWidth
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              error={!!dialogError}
              helperText={dialogError}
              inputProps={{ maxLength: NAME_MAX }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                色
              </Typography>
              <Box
                component="input"
                type="color"
                value={newColor}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewColor(e.target.value)}
                sx={{
                  width: 48,
                  height: 32,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 0,
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                }}
              />
              <Chip
                label={newName.trim() || 'プレビュー'}
                size="small"
                sx={{ borderRadius: 1.5, color: '#fff', bgcolor: newColor, fontWeight: 500 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || newName.trim() === ''}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpenseLabelSelect;
